import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import CreatorSearchStartWithAiConversation from "../../components/SingleProjectSelectedTasksBar/CreatorSearchStartWithAiConversation";
import useCheckWorkspacePermission from "src/hooks/useCheckWorkspacePermission";
import CreatorSearchAddToDropdown from "./CreatorSearchAddToDropdown";
import CreatorSearchAudienceOverlapButton from "./CreatorSearchAudienceOverlapButton";
import SelectedCreatorsBar from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/SelectedCreatorsBar";
import ClearSelectedButton from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/ClearSelectedButton";
import DownloadInPDFButton from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/DownloadInPDFButton";
import { generateSocialProfiles } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/utils";
import DownloadXlsxButton from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/components/SelectedCreatorsBar/DownloadXlsxButton";
import {
  CreatorShowcaseContext,
  openCreatorShowcase,
} from "src/app/CreatorShowcase/utils";
import { showErrorToast } from "src/utils/methods";

export default function CreatorSearchBar({
  clearAllSelected,
  workspaceUuid,
  projectId,
  handleOpenActivateProjectPane,
}) {
  const {
    searchAudienceResult,
    similarProfiles,
    selectedInfluencersOnResults,
    selectedInfluencersOnResultsUrls,
    searchAudienceInfo,
  } = useSelector((state) => state.creatorSearchReducer);

  const { projectBasicData } = useSelector((state) => state.projectReducer);

  const hasAccessToStartConversationWithAi =
    useCheckWorkspacePermission("AI_META_VALUE");

  const exportList = () => {
    const ids = selectedInfluencersOnResults.map(
      (id) =>
        [...searchAudienceResult, ...similarProfiles].find(
          (item) => item.userId === id,
        ).profileUrl,
    );

    if (ids.length === 0) {
      return showErrorToast();
    }

    openCreatorShowcase({
      history,
      context: CreatorShowcaseContext.CampaignCreatorSearch,
      creatorId: ids[0],
      ids: ids,
      workspaceId: workspaceUuid,
      projectId,
    });
  };

  // a stable array: the audience overlap panel recalculates its results whenever it changes
  const selectedCreators = useMemo(
    () =>
      selectedInfluencersOnResults
        .map((id) =>
          [...searchAudienceResult, ...similarProfiles].find(
            (item) => item.userId === id,
          ),
        )
        .filter(Boolean)
        .map((item) => ({
          id: item.userId,
          name: item.name || item.socialUsername,
          cover: item.profilePictureUrl,
          socialProfiles: generateSocialProfiles(item.socialProviders || {}),
        })),
    [selectedInfluencersOnResults, searchAudienceResult, similarProfiles],
  );

  if (selectedInfluencersOnResults.length === 0) return null;
  return (
    <SelectedCreatorsBar
      selectedCreatorsLength={selectedInfluencersOnResults.length}
    >
      {hasAccessToStartConversationWithAi && (
        <CreatorSearchStartWithAiConversation
          wsWorkspaceUuid={workspaceUuid}
          onClearAllSelected={clearAllSelected}
          wsProjectUuid={projectId}
          selectedInfluencersOnResultsUrls={selectedInfluencersOnResultsUrls}
          searchAudienceResult={searchAudienceResult}
          projectBasicData={projectBasicData}
        />
      )}
      <CreatorSearchAddToDropdown
        handleOpenActivateProjectPane={handleOpenActivateProjectPane}
      />
      <CreatorSearchAudienceOverlapButton
        creators={selectedCreators}
        workspaceUuid={workspaceUuid}
        projectId={projectId}
      />
      <DownloadInPDFButton
        exportList={exportList}
        selectedInfluencers={selectedInfluencersOnResults}
      />
      <DownloadXlsxButton
        selectedInfluencers={selectedInfluencersOnResults}
        pagination={searchAudienceInfo?.pagination}
        totalResults={searchAudienceInfo?.totalResults}
        projectId={projectId}
      />
      <ClearSelectedButton handleClearSelected={clearAllSelected} />
    </SelectedCreatorsBar>
  );
}
